import React from 'react';
import type { PlanningData, OnboardingAnalysisResult } from '../../types/onboarding';

interface AnalysisReportStepProps {
  planningData: PlanningData;
  analysisResult: OnboardingAnalysisResult;
  onReceiveReport: () => void;
  onContinue: () => void;
}

// --- Icons ---

const ProductIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CompetitorIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const BrandIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="10.5" r="2.5" />
    <circle cx="8.5" cy="7.5" r="2.5" />
    <circle cx="6.5" cy="12.5" r="2.5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

const CopyIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 6H3" />
    <path d="M21 12H3" />
    <path d="M15.5 18H3" />
  </svg>
);

const StrategyIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// --- Tailwind class constants (旧 styled-components) ---
// keyframes `slideUp` は globals.css の `obs-fade-in-up` へ移設。

const CONTAINER = 'w-full flex flex-col items-center py-4';

const TITLE = 'text-[1.375rem] leading-[1.4] font-bold text-gray-800 mb-2 text-center';

const SUBTITLE = 'text-[0.875rem] text-gray-500 mb-6 text-center';

const CARDS_CONTAINER =
  'w-full flex flex-col gap-4 mb-6 max-h-[400px] overflow-y-auto pr-1 ' +
  '[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent ' +
  '[&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-[2px]';

// animation-delay は連続値なので style={{ animationDelay }} でインライン指定
const CARD =
  'bg-white border border-gray-200 rounded-[10px] py-4 px-5 ' +
  'animate-[obs-fade-in-up_0.5s_ease-out_both]';

const CARD_HEADER = 'flex items-center gap-2.5 mb-3';

const CARD_ICON_WRAPPER =
  'w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0';

const CARD_TITLE = 'text-[0.9375rem] font-semibold text-gray-800 m-0';

const CARD_CONTENT = 'pl-[46px]';

const TAG_LIST = 'flex flex-wrap gap-1.5 mb-2';

const TAG =
  'inline-block py-[3px] px-2.5 rounded-xl text-[0.8125rem] leading-[1.4] bg-gray-100 text-gray-700';

const PURPLE_TAG =
  'inline-block py-[3px] px-2.5 rounded-xl text-[0.8125rem] leading-[1.4] bg-violet-100 text-violet-600';

const LABEL =
  'text-[0.75rem] text-gray-400 font-medium mb-1 mt-2 uppercase tracking-[0.03em] first:mt-0';

const TEXT_CONTENT = 'text-[0.8125rem] leading-[1.6] text-gray-700 m-0';

const COLOR_SWATCH_ROW = 'flex items-center gap-2 mt-1';

// background-color は任意の hex を props で受けるので style でインライン指定
const COLOR_SWATCH = 'w-6 h-6 rounded-md border border-black/10';

const COLOR_LABEL = 'text-[0.75rem] text-gray-500 font-[monospace]';

const BRAND_IMAGE_ROW = 'flex items-center gap-3 mb-2';

const BRAND_IMAGE = 'w-10 h-10 rounded-lg object-cover border border-gray-200';

const BRAND_NAME = 'text-[0.9375rem] font-semibold text-gray-800';

const QUOTE_BLOCK =
  'bg-purple-50 border-l-[3px] border-l-violet-600 py-2 px-3 rounded-r-md mb-1.5 ' +
  'text-[0.8125rem] leading-[1.5] text-gray-700';

const BUTTON_ROW = 'flex gap-3 w-full animate-[obs-fade-in-up_0.5s_ease-out_0.6s_both]';

const PRIMARY_BUTTON =
  'flex-1 bg-[linear-gradient(135deg,#7c3aed,#6d28d9)] text-white border-0 rounded-lg ' +
  'py-3 px-5 text-[0.9375rem] font-semibold cursor-pointer transition-all duration-200 ease-[ease] ' +
  'hover:bg-[linear-gradient(135deg,#6d28d9,#5b21b6)] hover:-translate-y-px ' +
  'hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)] active:translate-y-0';

const SECONDARY_BUTTON =
  'flex-1 bg-white text-violet-600 border border-violet-600 rounded-lg py-3 px-5 ' +
  'text-[0.9375rem] font-semibold cursor-pointer transition-all duration-200 ease-[ease] ' +
  'hover:bg-purple-50';

export const AnalysisReportStep: React.FC<AnalysisReportStepProps> = ({
  planningData,
  analysisResult,
  onReceiveReport,
  onContinue,
}) => {
  const { product_features, competitor_info, brand_info, key_copies, ad_strategy } = planningData;

  return (
    <div className={CONTAINER}>
      <h2 className={TITLE}>分析レポート</h2>
      <p className={SUBTITLE}>AIがあなたのサービスを分析しました</p>

      <div className={CARDS_CONTAINER}>
        {/* 1. Product Features */}
        <div className={CARD} style={{ animationDelay: '0s' }}>
          <div className={CARD_HEADER}>
            <div className={CARD_ICON_WRAPPER}>
              <ProductIcon />
            </div>
            <h3 className={CARD_TITLE}>商品特徴</h3>
          </div>
          <div className={CARD_CONTENT}>
            <div className={LABEL}>特徴</div>
            <div className={TAG_LIST}>
              {product_features.features.map((feature, i) => (
                <span className={TAG} key={i}>{feature}</span>
              ))}
            </div>
            <div className={LABEL}>メリット</div>
            <div className={TAG_LIST}>
              {product_features.benefits.map((benefit, i) => (
                <span className={PURPLE_TAG} key={i}>{benefit}</span>
              ))}
            </div>
          </div>
        </div>

        {/* 2. Competitive Differentiation */}
        <div className={CARD} style={{ animationDelay: '0.1s' }}>
          <div className={CARD_HEADER}>
            <div className={CARD_ICON_WRAPPER}>
              <CompetitorIcon />
            </div>
            <h3 className={CARD_TITLE}>競合との差別化</h3>
          </div>
          <div className={CARD_CONTENT}>
            <div className={LABEL}>業界</div>
            <p className={TEXT_CONTENT}>{competitor_info.industry}</p>
            <div className={LABEL}>競合スタイル</div>
            <div className={TAG_LIST}>
              {competitor_info.competitor_styles.map((style, i) => (
                <span className={TAG} key={i}>{style}</span>
              ))}
            </div>
            <div className={LABEL}>差別化ポイント</div>
            <div className={TAG_LIST}>
              {competitor_info.differentiation_points.map((point, i) => (
                <span className={PURPLE_TAG} key={i}>{point}</span>
              ))}
            </div>
          </div>
        </div>

        {/* 3. Brand Info */}
        <div className={CARD} style={{ animationDelay: '0.2s' }}>
          <div className={CARD_HEADER}>
            <div className={CARD_ICON_WRAPPER}>
              <BrandIcon />
            </div>
            <h3 className={CARD_TITLE}>ブランド情報</h3>
          </div>
          <div className={CARD_CONTENT}>
            <div className={BRAND_IMAGE_ROW}>
              {(brand_info.logo_url || analysisResult.logo_url) && (
                <img
                  className={BRAND_IMAGE}
                  src={brand_info.logo_url || analysisResult.logo_url || ''}
                  alt="Brand logo"
                />
              )}
              <span className={BRAND_NAME}>{brand_info.brand_name}</span>
            </div>
            {brand_info.colors.length > 0 && (
              <>
                <div className={LABEL}>ブランドカラー</div>
                <div className={COLOR_SWATCH_ROW}>
                  {brand_info.colors.map((color, i) => (
                    <React.Fragment key={i}>
                      <div className={COLOR_SWATCH} style={{ backgroundColor: color }} />
                      <span className={COLOR_LABEL}>{color}</span>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 4. Key Copy */}
        <div className={CARD} style={{ animationDelay: '0.3s' }}>
          <div className={CARD_HEADER}>
            <div className={CARD_ICON_WRAPPER}>
              <CopyIcon />
            </div>
            <h3 className={CARD_TITLE}>推奨キーコピー</h3>
          </div>
          <div className={CARD_CONTENT}>
            <div className={LABEL}>ヘッドライン</div>
            {key_copies.headlines.map((headline, i) => (
              <div className={QUOTE_BLOCK} key={i}>{headline}</div>
            ))}
            <div className={LABEL}>CTA</div>
            <div className={TAG_LIST}>
              {key_copies.cta_texts.map((cta, i) => (
                <span className={PURPLE_TAG} key={i}>{cta}</span>
              ))}
            </div>
            {key_copies.recommended_messages.length > 0 && (
              <>
                <div className={LABEL}>推奨メッセージ</div>
                {key_copies.recommended_messages.map((msg, i) => (
                  <div className={QUOTE_BLOCK} key={i}>{msg}</div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* 5. Ad Strategy */}
        <div className={CARD} style={{ animationDelay: '0.4s' }}>
          <div className={CARD_HEADER}>
            <div className={CARD_ICON_WRAPPER}>
              <StrategyIcon />
            </div>
            <h3 className={CARD_TITLE}>推奨広告戦略</h3>
          </div>
          <div className={CARD_CONTENT}>
            <div className={LABEL}>ターゲットオーディエンス</div>
            <p className={TEXT_CONTENT}>{ad_strategy.target_audience}</p>
            <div className={LABEL}>推奨スタイル</div>
            <div className={TAG_LIST}>
              {ad_strategy.recommended_styles.map((style, i) => (
                <span className={TAG} key={i}>{style}</span>
              ))}
            </div>
            <div className={LABEL}>キーセリングポイント</div>
            <div className={TAG_LIST}>
              {ad_strategy.key_selling_points.map((point, i) => (
                <span className={PURPLE_TAG} key={i}>{point}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={BUTTON_ROW}>
        <button className={SECONDARY_BUTTON} type="button" onClick={onReceiveReport}>
          レポートを受け取る
        </button>
        <button className={PRIMARY_BUTTON} type="button" onClick={onContinue}>
          次へ進む
        </button>
      </div>
    </div>
  );
};

export default AnalysisReportStep;
